import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import useScopedTranslation from 'hooks/useScopedTranslation';
import configWritingsI18n from './i18n';
import './configWritings.scss';
import PlantillasPanel from './panels/PlantillasPanel';
import ExcepcionesPanel from './panels/ExcepcionesPanel';
import AbandonoPanel from './panels/AbandonoPanel';
import ApelacionPanel from './panels/ApelacionPanel';

/**
 * Pestaña de escritos judiciales, con una sub-pestaña por camino.
 *
 * Son cuatro y se dividen en dos grupos:
 *
 * - PLANTILLAS: los escritos de trámite. No hay nada que razonar, solo rellenar datos de la
 *   causa. Es el símil de Smart Docs.
 * - Los otros tres son ANÁLISIS CON IA, y son tres porque son tres momentos distintos del juicio
 *   ejecutivo: defenderse al ser notificado (excepciones), aprovechar la inactividad del
 *   ejecutante (abandono), o recurrir tras un fallo adverso (apelación). En una causa dada
 *   normalmente solo uno aplica.
 *
 * No lleva Card: el contenido de la pestaña ya vive dentro del Card del detalle de causa
 * (`activity-card`), y anidar tarjetas duplica los bordes.
 */
const CaseWritings = () => {
  const { DesignSystemComponents, caseData } = useOutletContext();
  const { t } = useScopedTranslation(configWritingsI18n);
  const [activa, setActiva] = useState('abandono');

  const subTabs = useMemo(
    () => [
      { key: 'plantillas', label: t('cw_tab_templates') },
      { key: 'excepciones', label: t('cw_tab_exceptions') },
      { key: 'abandono', label: t('cw_tab_abandonment') },
      { key: 'apelacion', label: t('cw_tab_appeal') },
    ],
    [t]
  );

  const panelProps = { DesignSystemComponents, caseData, t };

  // El Card padre no lleva padding: cada pestaña pone el suyo. CaseTasks usa px-4,
  // CaseMovements mt-3 y CaseOldMovements my-4. Acá se sigue el mismo criterio.
  return (
    <div className='case-writings-tab px-4 pt-3 pb-4'>
      <nav
        role='tablist'
        aria-label={t('cw_subTabsAriaLabel')}
        style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}
        className='pb-3'
      >
        {subTabs.map((tab) => (
          <button
            key={tab.key}
            type='button'
            role='tab'
            aria-selected={activa === tab.key}
            onClick={() => setActiva(tab.key)}
            className={'navbar-item ' + (activa === tab.key ? 'isActive' : '')}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activa === 'plantillas' && <PlantillasPanel {...panelProps} />}
      {activa === 'excepciones' && <ExcepcionesPanel {...panelProps} />}
      {activa === 'abandono' && <AbandonoPanel {...panelProps} />}
      {activa === 'apelacion' && <ApelacionPanel {...panelProps} />}
    </div>
  );
};

export default CaseWritings;
